import { Plugs } from '@phosphor-icons/react'
import { useAccountSecurity } from 'Hooks'
import { COLOR_RED } from 'constant/colors'
import { Loading } from 'Organisms/Loading'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import ModalConfirmOrCancel from 'Organisms/ModalConfirmOrCancel'
import { GoogleBrandIcon } from 'Organisms/Svg'
import { useEffect, useState } from 'react'
import SetPasswordModal from './SetPasswordModal'

const PROVIDERS = {
  Google: { label: 'Google', icon: GoogleBrandIcon }
}

const ConnectedAccountsSettings = () => {
  const { loadingAccountSecurityHook, connectedAccountsHook, getConnectedAccountsHook, disconnectConnectedAccountHook } = useAccountSecurity()

  const [confirmDisconnect, setConfirmDisconnect] = useState(null)
  const [showSetPassword, setShowSetPassword] = useState(false)
  const [message, setMessage] = useState({ open: false })
  const [loadError, setLoadError] = useState(null)

  const loadConnectedAccounts = async () => {
    const res = await getConnectedAccountsHook()
    setLoadError(res.success ? null : res.message)
  }

  useEffect(() => {
    loadConnectedAccounts()
  }, [])

  const handleDisconnectClick = (provider) => {
    if (!connectedAccountsHook.has_password) {
      return setShowSetPassword(true)
    }

    setConfirmDisconnect(provider)
  }

  const handleConfirmDisconnect = async () => {
    const provider = confirmDisconnect
    const res = await disconnectConnectedAccountHook({ provider })

    if (!res.success) {
      return setMessage({ open: true, error: true, message: res.message })
    }

    await loadConnectedAccounts()
    setMessage({ open: true, message: `Your ${provider} account has been disconnected.` })
  }

  const handlePasswordSet = async () => {
    await loadConnectedAccounts()
    setMessage({ open: true, message: 'Your password has been set. You can now disconnect your Google account.' })
  }

  const connectedProviders = connectedAccountsHook.connected_accounts.filter(({ provider }) => PROVIDERS[provider])

  if (!loadingAccountSecurityHook && !loadError && connectedProviders.length === 0) { return null }

  return (
    <div className="d-grid align-content-start">
      {loadingAccountSecurityHook && <Loading />}

      <div className="row mb-4">
        <div className="col-12">
          <h4>Connected accounts</h4>
        </div>
      </div>

      {loadError
        ? (
        <div className="settings_card">
          <div className="settings_card_body d-grid gap-3">
            <p className="text-danger m-0">{loadError}</p>
            <div>
              <button type="button" className="btn btn-secondary" onClick={loadConnectedAccounts}>
                Retry
              </button>
            </div>
          </div>
        </div>
          )
        : (
        <div className="settings_card">
          <div className="settings_card_body d-grid gap-3">
            {connectedProviders.map(({ provider }) => {
              const { label, icon: Icon } = PROVIDERS[provider] ?? { label: provider }

              return (
                <div className="row align-items-center" key={provider}>
                  <div className="col d-flex align-items-center">
                    {Icon && <Icon size={20} className="me-2" />}
                    <h6 className="m-0">{label}</h6>
                  </div>
                  <div className="col-auto">
                    <button type="button" className="btn btn-danger" onClick={() => handleDisconnectClick(provider)}>
                      Disconnect
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
          )}

      {confirmDisconnect && (
        <ModalConfirmOrCancel
          title={`Are you sure you want to disconnect your ${confirmDisconnect} account? You'll sign in with your email and password instead.`}
          setVisible={() => setConfirmDisconnect(null)}
          btnConfirm="Disconnect"
          onConfirm={handleConfirmDisconnect}
          icon={<Plugs size={56} color={COLOR_RED} weight="bold" />}
          style="danger"
        />
      )}

      {showSetPassword && (
        <SetPasswordModal onClose={() => setShowSetPassword(false)} onSuccess={handlePasswordSet} />
      )}

      {message.open && (
        <ModalSuccess
          visible={true}
          setVisible={() => setMessage({ open: false })}
          title={message.message}
          identifier="modalSuccessConnectedAccounts"
          error={message.error}
        />
      )}
    </div>
  )
}

export default ConnectedAccountsSettings
